import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../animations/variants';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  AdminPanelSettings as AdminIcon,
  Login as LoginIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Key as KeyIcon,
  Person as PersonIcon,
  Domain as DomainIcon,
  LockReset as LockResetIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useApi } from '../hooks/useApi';
import apiService from '../services/api';
import { toast } from 'react-toastify';
import { isPasswordValid, getPasswordRequirements } from '../utils/passwordValidation';

// Shared input styles for consistency
const inputStyles = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    borderRadius: '6px',
    fontFamily: 'Metropolis, sans-serif',
    '& fieldset': {
      borderColor: '#E5E5E5',
    },
    '&:hover fieldset': {
      borderColor: '#D9DBDC',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4698D3',
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#666666',
    '&.Mui-focused': {
      color: '#4698D3',
    },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#999999',
  },
};

// Shared dialog styles
const dialogPaperProps = {
  sx: {
    borderRadius: '12px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E5E5',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  },
};

const CustomerDashboard = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { getCustomer, resetCustomerAdmin } = useApi();

  const [customer, setCustomer] = useState(null);
  const [realmStatus, setRealmStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInitForm, setShowInitForm] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');

  // Reset admin dialog state
  const [showResetAdminDialog, setShowResetAdminDialog] = useState(false);
  const [resettingAdmin, setResettingAdmin] = useState(false);
  const [resetForm, setResetForm] = useState({
    newPassword: '',
    confirmPassword: '',
    resetMFA: false,
  });

  useEffect(() => {
    loadCustomer();
  }, [customerId]);

  const loadCustomer = async () => {
    try {
      const customerData = await getCustomer(customerId);
      setCustomer(customerData);

      const statusResponse = await apiService.getCustomerRealmStatus(customerId);
      if (statusResponse.success) {
        setRealmStatus(statusResponse.data);
      }
    } catch (err) {
      console.error('Failed to load customer:', err);
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  const handleInitAdmin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await apiService.initializeCustomerAdmin(customerId, adminPassword);
      if (response.success) {
        toast.success('Customer admin initialized successfully');
        setShowInitForm(false);
        setAdminPassword('');
        await loadCustomer();
      }
    } catch (err) {
      toast.error('Failed to initialize customer admin: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerLogin = async () => {
    try {
      const response = await apiService.impersonateCustomerAdmin(customer.id);
      const data = response.data || response;

      // Build impersonation payload to pass via cookie (localStorage is per-origin)
      const impersonationData = {
        authToken: data.accessToken || data.token,
        refreshToken: data.refreshToken || '',
        tokenExpiry: data.accessExpiresAt || data.expiresAt || '',
        adminDetails: {
          username: data.username,
          role: data.role,
          isAdmin: data.isAdmin,
          customerId: data.customerId,
        },
        impersonating: {
          customerName: data.customerName || customer.name,
          customerId: data.customerId || customer.id,
        },
      };

      // Set cookie on parent domain so it's accessible on customer subdomain
      const currentHost = window.location.hostname;
      // Extract the base domain for cookie (e.g., prism.cloudkeeper.com from prism.cloudkeeper.com)
      const cookieDomain = '.' + currentHost;
      const cookieValue = encodeURIComponent(JSON.stringify(impersonationData));
      document.cookie = `impersonate_session=${cookieValue}; domain=${cookieDomain}; path=/; max-age=60; secure; samesite=lax`;

      // Build customer subdomain URL and redirect
      const protocol = window.location.protocol;

      if (window.location.hostname.includes('localhost')) {
        // For localhost, store directly in localStorage since same origin
        localStorage.setItem('authToken', impersonationData.authToken);
        localStorage.setItem('refreshToken', impersonationData.refreshToken);
        localStorage.setItem('adminDetails', JSON.stringify(impersonationData.adminDetails));
        localStorage.setItem('impersonating', JSON.stringify(impersonationData.impersonating));
        navigate(`/?customer=${customer.realmName}`);
        return;
      }

      const port = window.location.port ? `:${window.location.port}` : '';
      const customerUrl = `${protocol}//${customer.realmName}.${currentHost}${port}`;

      window.location.href = customerUrl;
    } catch (err) {
      console.error('Failed to impersonate customer admin:', err);
      toast.error(err.message || 'Failed to impersonate customer admin');
    }
  };

  // Reset Admin Dialog handlers
  const handleOpenResetDialog = () => {
    setResetForm({ newPassword: '', confirmPassword: '', resetMFA: false });
    setShowResetAdminDialog(true);
  };

  const handleCloseResetDialog = () => {
    setShowResetAdminDialog(false);
    setResetForm({ newPassword: '', confirmPassword: '', resetMFA: false });
  };

  const handleResetAdmin = async () => {
    // Validate inputs
    if (!resetForm.newPassword && !resetForm.resetMFA) {
      toast.error('Please provide a new password and/or select to reset MFA');
      return;
    }

    if (resetForm.newPassword && resetForm.newPassword !== resetForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (resetForm.newPassword && !isPasswordValid(resetForm.newPassword)) {
      toast.error('Password does not meet requirements');
      return;
    }

    setResettingAdmin(true);
    try {
      const result = await resetCustomerAdmin(customerId, {
        newPassword: resetForm.newPassword || undefined,
        resetMFA: resetForm.resetMFA,
      });
      if (result.success) {
        const actions = [];
        if (result.data?.passwordReset) actions.push('password');
        if (result.data?.mfaReset) actions.push('MFA');
        toast.success(`Successfully reset ${actions.join(' and ')} for ${customer.name}'s admin`);
        handleCloseResetDialog();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to reset admin credentials');
      console.error('Failed to reset admin:', err);
    } finally {
      setResettingAdmin(false);
    }
  };

  if (loading || !customer) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#F9FBFD',
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: '#4698D3' }} />
        <Typography
          sx={{
            color: '#666666',
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 500,
          }}
        >
          Loading customer data...
        </Typography>
      </Box>
    );
  }

  const adminInitialized = realmStatus?.customer?.adminInitialized || false;

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 32,
        paddingRight: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
      {/* Back Button */}
      <Button
        onClick={() => navigate('/customers')}
        startIcon={<ArrowBackIcon />}
        sx={{
          color: '#666666',
          fontFamily: 'Metropolis, sans-serif',
          fontWeight: 500,
          textTransform: 'none',
          mb: 3,
          '&:hover': {
            backgroundColor: '#F0F4F8',
            color: '#253E66',
          },
        }}
      >
        Back to Customers
      </Button>

      {/* Hero Section */}
      <motion.div variants={sectionVariants}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar
            sx={{
              backgroundColor: '#FFFFFF',
              width: 64,
              height: 64,
              border: '1px solid #E5E5E5',
            }}
          >
            <img
              src="/ck-auth-logo.png"
              alt="CK Prism"
              style={{
                width: '80%',
                height: '80%',
                objectFit: 'contain'
              }}
            />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'Bitter, Georgia, serif',
                fontWeight: 600,
                fontSize: '1.75rem',
                color: '#253E66',
                mb: 0.5,
              }}
            >
              {customer.name}
            </Typography>
            <Typography
              sx={{
                color: '#999999',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 500,
                fontSize: '0.875rem',
                mb: 1.5,
              }}
            >
              {customer.description || 'Customer dashboard and management'}
            </Typography>
            <Chip
              label={adminInitialized ? 'Admin Initialized' : 'Admin Not Initialized'}
              icon={adminInitialized ? <CheckCircleIcon /> : <WarningIcon />}
              sx={{
                backgroundColor: adminInitialized ? 'rgba(39, 174, 96, 0.1)' : 'rgba(243, 156, 18, 0.1)',
                color: adminInitialized ? '#27AE60' : '#F39C12',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 500,
                '& .MuiChip-icon': {
                  color: adminInitialized ? '#27AE60' : '#F39C12',
                },
              }}
            />
          </Box>
        </Box>
      </Paper>
      </motion.div>

      {/* Customer Information Card */}
      <motion.div variants={sectionVariants}>
      <Card
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
          mb: 3,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <PersonIcon sx={{ fontSize: 24, color: '#4698D3' }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                color: '#253E66',
              }}
            >
              Customer Information
            </Typography>
          </Box>

          <Divider sx={{ mb: 3, borderColor: '#E5E5E5' }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#F9FBFD', borderRadius: '6px', border: '1px solid #E5E5E5' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    letterSpacing: '0.5px',
                  }}
                >
                  Customer Name
                </Typography>
                <Typography
                  sx={{
                    color: '#253E66',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    mt: 0.5,
                  }}
                >
                  {customer.name}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#F9FBFD', borderRadius: '6px', border: '1px solid #E5E5E5' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    letterSpacing: '0.5px',
                  }}
                >
                  Realm Name
                </Typography>
                <Typography
                  sx={{
                    color: '#253E66',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    mt: 0.5,
                  }}
                >
                  {customer.realmName}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#F9FBFD', borderRadius: '6px', border: '1px solid #E5E5E5' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    letterSpacing: '0.5px',
                  }}
                >
                  Customer ID
                </Typography>
                <Typography
                  sx={{
                    color: '#253E66',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    mt: 0.5,
                    fontSize: '0.85rem',
                  }}
                >
                  {customer.id}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#F9FBFD', borderRadius: '6px', border: '1px solid #E5E5E5' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    letterSpacing: '0.5px',
                  }}
                >
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={adminInitialized ? 'Ready' : 'Needs Setup'}
                    size="small"
                    sx={{
                      backgroundColor: adminInitialized ? 'rgba(39, 174, 96, 0.1)' : 'rgba(243, 156, 18, 0.1)',
                      color: adminInitialized ? '#27AE60' : '#F39C12',
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 500,
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      </motion.div>

      {/* Admin Initialization Section */}
      {!adminInitialized && (
        <motion.div variants={sectionVariants}>
        <Card
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #F39C12',
            borderRadius: '8px',
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <AdminIcon sx={{ fontSize: 24, color: '#F39C12' }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  color: '#253E66',
                }}
              >
                Initialize Customer Admin
              </Typography>
            </Box>

            <Divider sx={{ mb: 3, borderColor: '#E5E5E5' }} />

            <Alert
              severity="warning"
              sx={{
                mb: 3,
                backgroundColor: 'rgba(243, 156, 18, 0.08)',
                border: '1px solid rgba(243, 156, 18, 0.2)',
                '& .MuiAlert-icon': { color: '#F39C12' },
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: 'Metropolis, sans-serif', color: '#253E66' }}
              >
                This customer needs an admin user to be initialized before it can be used. Create the initial admin account with mandatory MFA setup to get started.
              </Typography>
            </Alert>

            {!showInitForm ? (
              <Button
                variant="contained"
                startIcon={<AdminIcon />}
                onClick={() => setShowInitForm(true)}
                sx={{
                  backgroundColor: '#F39C12',
                  color: '#FFFFFF',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: '#E67E22',
                  },
                }}
              >
                Initialize Admin
              </Button>
            ) : (
              <Box component="form" onSubmit={handleInitAdmin} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label="Admin Password"
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Min 8 chars, uppercase, lowercase, number, special char"
                  required
                  disabled={loading}
                  helperText={adminPassword && !isPasswordValid(adminPassword) ? getPasswordRequirements().join(', ') : 'After initialization, MFA setup will be mandatory before the admin can login.'}
                  InputProps={{
                    startAdornment: <KeyIcon sx={{ color: '#999999', mr: 1 }} />,
                  }}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !isPasswordValid(adminPassword)}
                    sx={{
                      backgroundColor: '#F39C12',
                      color: '#FFFFFF',
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '6px',
                      flex: 1,
                      '&:hover': {
                        backgroundColor: '#E67E22',
                      },
                      '&:disabled': {
                        backgroundColor: '#E5E5E5',
                        color: '#999999',
                      },
                    }}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AdminIcon />}
                  >
                    {loading ? 'Initializing...' : 'Initialize Admin'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setShowInitForm(false)}
                    disabled={loading}
                    sx={{
                      borderColor: '#E5E5E5',
                      color: '#666666',
                      fontFamily: 'Metropolis, sans-serif',
                      textTransform: 'none',
                      borderRadius: '6px',
                      '&:hover': {
                        borderColor: '#D9DBDC',
                        backgroundColor: '#F9FBFD',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
        </motion.div>
      )}

      {/* Customer Actions Section */}
      {adminInitialized && (
        <motion.div variants={sectionVariants}>
        <Card
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #27AE60',
            borderRadius: '8px',
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <LoginIcon sx={{ fontSize: 24, color: '#27AE60' }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  color: '#253E66',
                }}
              >
                Customer Actions
              </Typography>
            </Box>

            <Divider sx={{ mb: 3, borderColor: '#E5E5E5' }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  startIcon={<LoginIcon />}
                  onClick={handleCustomerLogin}
                  fullWidth
                  sx={{
                    backgroundColor: '#27AE60',
                    color: '#FFFFFF',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#219A52',
                    },
                  }}
                >
                  Login as Customer
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  startIcon={<LockResetIcon />}
                  onClick={handleOpenResetDialog}
                  fullWidth
                  sx={{
                    backgroundColor: '#F39C12',
                    color: '#FFFFFF',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#E67E22',
                    },
                  }}
                >
                  Reset Admin Credentials
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        </motion.div>
      )}

      {/* Realm Status Section */}
      {realmStatus && (
        <motion.div variants={sectionVariants}>
        <Card
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <DomainIcon sx={{ fontSize: 24, color: '#4698D3' }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  color: '#253E66',
                }}
              >
                Realm Status
              </Typography>
            </Box>

            <Divider sx={{ mb: 3, borderColor: '#E5E5E5' }} />

            <Box
              sx={{
                backgroundColor: '#F9FBFD',
                borderRadius: '6px',
                p: 2,
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                overflow: 'auto',
                maxHeight: '300px',
                border: '1px solid #E5E5E5',
              }}
            >
              <pre style={{ margin: 0, color: '#253E66', whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(realmStatus, null, 2)}
              </pre>
            </Box>
          </CardContent>
        </Card>
        </motion.div>
      )}

      {/* Reset Admin Dialog */}
      <Dialog
        open={showResetAdminDialog}
        onClose={handleCloseResetDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle
          sx={{
            backgroundColor: 'rgba(243, 156, 18, 0.08)',
            borderBottom: '1px solid rgba(243, 156, 18, 0.2)',
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#F39C12',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2,
          }}
        >
          <Avatar sx={{ backgroundColor: 'rgba(243, 156, 18, 0.15)', width: 36, height: 36 }}>
            <LockResetIcon sx={{ color: '#F39C12', fontSize: 20 }} />
          </Avatar>
          Reset Admin Credentials
          <IconButton
            onClick={handleCloseResetDialog}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: '#F39C12',
              backgroundColor: 'rgba(243, 156, 18, 0.1)',
              border: '1px solid rgba(243, 156, 18, 0.2)',
              width: 32,
              height: 32,
              '&:hover': {
                backgroundColor: 'rgba(243, 156, 18, 0.15)',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              backgroundColor: 'rgba(243, 156, 18, 0.08)',
              border: '1px solid rgba(243, 156, 18, 0.2)',
              '& .MuiAlert-icon': { color: '#F39C12' },
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: 'Metropolis, sans-serif', color: '#253E66' }}>
              This will reset the super admin credentials for <strong>{customer?.name}</strong>.
              The admin will need to use the new password and/or set up MFA again.
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                color: '#253E66',
              }}
            >
              Reset Options
            </Typography>

            <TextField
              label="New Password"
              type="password"
              value={resetForm.newPassword}
              onChange={(e) => setResetForm({ ...resetForm, newPassword: e.target.value })}
              fullWidth
              placeholder="Leave empty to keep current password"
              helperText={resetForm.newPassword && !isPasswordValid(resetForm.newPassword)
                ? getPasswordRequirements().join(', ')
                : 'Password must be at least 8 characters with uppercase, lowercase, number, and special character'}
              error={resetForm.newPassword && !isPasswordValid(resetForm.newPassword)}
              sx={inputStyles}
            />

            <TextField
              label="Confirm New Password"
              type="password"
              value={resetForm.confirmPassword}
              onChange={(e) => setResetForm({ ...resetForm, confirmPassword: e.target.value })}
              fullWidth
              disabled={!resetForm.newPassword}
              error={resetForm.confirmPassword && resetForm.newPassword !== resetForm.confirmPassword}
              helperText={resetForm.confirmPassword && resetForm.newPassword !== resetForm.confirmPassword ? 'Passwords do not match' : ''}
              sx={inputStyles}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={resetForm.resetMFA}
                  onChange={(e) => setResetForm({ ...resetForm, resetMFA: e.target.checked })}
                  sx={{
                    color: '#F39C12',
                    '&.Mui-checked': {
                      color: '#F39C12',
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, color: '#253E66' }}>
                    Reset MFA
                  </Typography>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#999999' }}>
                    This will disable MFA and require the admin to set it up again on next login
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', ml: 0 }}
            />
          </Box>
          </motion.div>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, gap: 1.5, backgroundColor: '#F9FBFD', borderTop: '1px solid #E5E5E5' }}>
          <Button
            onClick={handleCloseResetDialog}
            disabled={resettingAdmin}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              textTransform: 'none',
              color: '#666666',
              borderColor: '#E5E5E5',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#D9DBDC',
                backgroundColor: '#FFFFFF',
              },
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleResetAdmin}
            disabled={resettingAdmin || (!resetForm.newPassword && !resetForm.resetMFA) || (resetForm.newPassword && resetForm.newPassword !== resetForm.confirmPassword) || (resetForm.newPassword && !isPasswordValid(resetForm.newPassword))}
            sx={{
              backgroundColor: '#F39C12',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              boxShadow: '0 2px 8px rgba(243, 156, 18, 0.25)',
              '&:hover': {
                backgroundColor: '#E67E22',
                boxShadow: '0 4px 12px rgba(243, 156, 18, 0.35)',
              },
              '&:disabled': {
                backgroundColor: '#E5E5E5',
                color: '#999999',
              },
            }}
            startIcon={resettingAdmin ? <CircularProgress size={16} color="inherit" /> : <LockResetIcon />}
          >
            {resettingAdmin ? 'Resetting...' : 'Reset Admin Credentials'}
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default CustomerDashboard;
